import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "https://dashboard.lono.co.in/api";

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json", // Default content type
  },
});
const getAuthHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("staffToken"); // Adjust token key if necessary
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

// Add a request interceptor to include the token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token"); // Retrieve token from localStorage
    if (token) {
      config.headers.Authorization = `Bearer ${token}`; // Add Authorization header
    }
    return config;
  },
  (error) => Promise.reject(error) // Handle request error
);

export const affiliateAPI = {
  // Get all affiliate partners
  getAll: async () => {
    try {
      console.log("Fetching all affiliate partners...");
      const response = await api.get("/affiliate-partners");
      console.log("Raw API Response:", response.data);

      if (!response.data) {
        throw new Error("No data received from server");
      }

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch partners");
      }

      return response.data;
    } catch (error) {
      console.error("Error in getAll:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  },

  // Get affiliate partner by ID
  getById: async (id) => {
    try {
      const response = await api.get(`/affiliate-partners/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  // Update staff member
  // updateAffiliatePartner: async (AffiliateId, updateData) => {
  //   try {
  //     console.log("AffiliateId being sent to API:", AffiliateId,updateData);

  //     // If updateData is FormData (with files), axios must send it as multipart
  //     const response = await axios.put(
  //       `${API_URL}/affiliate-partners/${AffiliateId}/update`,
  //       updateData,
  //       {
  //         headers: {
  //           "Content-Type": "multipart/form-data",
  //           // ...(getAuthHeaders() || {}) // if you need auth
  //         },
  //       }
  //     );

  //     return response.data;
  //   } catch (error) {
  //     console.error(
  //       "updateAffiliatePartner error:",
  //       error.response?.data || error.message
  //     );
  //     throw error.response?.data || error.message;
  //   }
  // },
   updateAffiliatePartner: async (AffiliateId, updateData) => {
    // updateData might include profilePicture as a Base64 string
    try {
      console.log("AffiliateId being sent to API:", AffiliateId);
      const response = await axios.put(`${API_URL}/affiliate-partners/${AffiliateId}/update`, updateData,);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new affiliate partner
  create: async (affiliateData) => {
    try {
      const response = await api.post("/affiliate-partners", affiliateData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update affiliate partner
  update: async (id, affiliateData) => {
    try {
      const response = await api.put(
        `/affiliate-partners/${id}`,
        affiliateData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete affiliate partner
  delete: async (id) => {
    try {
      const response = await api.delete(`/affiliate-partners/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get affiliate statistics
  getStats: async () => {
    try {
      console.log("Fetching affiliate stats...");
      const response = await api.get("/affiliate-partners", {
        params: { stats: true },
      });
      console.log("Raw Stats Response:", response.data);

      if (!response.data) {
        throw new Error("No data received from server");
      }

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch stats");
      }

      return response.data;
    } catch (error) {
      console.error("Error in getStats:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  },

  // Update affiliate status
  updateStatus: async (id, status) => {
    try {
      const response = await api.patch(`/affiliate-partners/${id}/status`, {
        status,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload document
  uploadDocument: async (file, type, affiliateId) => {
    try {
      console.log("Uploading document:", { file, type, affiliateId });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type); // NOTE: sending 'type' not 'documentType'

      console.log("FormData contents:", {
        file: file.name,
        type,
        affiliateId,
      });

      const response = await api.post(
        `/affiliate-partners/upload/${affiliateId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to upload document");
      }

      return response.data;
    } catch (error) {
      console.error(
        "Upload error details:",
        error.response?.data || error.message
      );
      throw error.response?.data || error.message;
    }
  },

  // Search affiliate partners
  search: async (query) => {
    try {
      const response = await api.get("/affiliate-partners/search", {
        params: { query },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Export affiliate data
  exportData: async (format = "csv") => {
    try {
      const response = await api.get(`/affiliate-partners/export/${format}`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get affiliate partner profile by ID
  getAffiliateProfile: async (id) => {
    try {
      // Add validation before making the API call
      if (!id || id === "undefined") {
        throw new Error("Invalid partner ID");
      }

      console.log("Fetching affiliate profile for ID:", id);
      const response = await api.get(`/affiliate-partners/${id}/profile`);
      console.log("API Response:", response.data);

      if (!response.data) {
        throw new Error("No data received from server");
      }

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to fetch profile data"
        );
      }

      return response.data;
    } catch (error) {
      console.error("Error in getAffiliateProfile:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  },

  // Get affiliate partner's applications
  getAffiliateApplications: async (id) => {
    try {
      const response = await api.get(`/affiliate-partners/${id}/applications`);
      return response.data;
    } catch (error) {
      console.error("Error fetching affiliate applications:", error);
      throw error;
    }
  },

  // Update affiliate partner status
  updateAffiliateStatus: async (id, status) => {
    try {
      const response = await api.patch(`/affiliate-partners/${id}/status`, {
        status,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating affiliate status:", error);
      throw error;
    }
  },

  // Download affiliate documents
  downloadAffiliateDocument: async (id, documentType) => {
    try {
      const response = await api.get(
        `/affiliate-partners/${id}/documents/${documentType}`,
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error downloading document:", error);
      throw error;
    }
  },

  // Verify document
  verifyDocument: async (affiliateId, documentType, status) => {
    try {
      console.log("Verifying document:", { affiliateId, documentType, status });
      const response = await api.patch(
        `/affiliate-partners/${affiliateId}/documents/verify`,
        {
          documentType,
          status,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to verify document");
      }

      return response.data;
    } catch (error) {
      console.error("Error verifying document:", error);
      throw error.response?.data || error.message;
    }
  },

  // Get customers referred by an affiliate partner
  getAffiliateCustomers: async (partnerId) => {
    try {
      if (!partnerId || partnerId === "undefined") {
        throw new Error("Invalid partner ID");
      }
      const response = await api.get(
        `/affiliate-partners/${partnerId}/customers`
      );
      if (!response.data) {
        throw new Error("No data received from server");
      }
      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch customers");
      }
      return response.data;
    } catch (error) {
      console.error("Error in getAffiliateCustomers:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
      }
      throw error;
    }
  },

  // Get current user's affiliate partner profile
  getCurrentUserProfile: async () => {
    try {
      const response = await api.get("/affiliate-partners/profile/me");
      if (!response.data) {
        throw new Error("No data received from server");
      }
      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to fetch profile data"
        );
      }
      return response.data;
    } catch (error) {
      console.error("Error in getCurrentUserProfile:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  },

  // Update bank details for an affiliate partner
  updateBankDetails: async (partnerId, bankDetails) => {
    try {
      const response = await api.put(
        `/affiliate-partners/${partnerId}/bank-details`,
        bankDetails
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get dashboard stats for affiliate partner (pie charts)
  getAffiliateDashboardStats: async (id) => {
    try {
      const response = await api.get(`/affiliate-partners/dashboard-stats`);
      return response.data;
    } catch (error) {
      console.error("Error fetching affiliate dashboard stats:", error);
      throw error;
    }
  },

  // Create a new network
  createNetwork: async (networkData) => {
    try {
      const response = await api.post(
        "/affiliate-partners/network",
        networkData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get affiliate pie chart data
  getAffiliatePieChartData: async (type = "loanTypeCounts", period = "all") => {
    try {
      const response = await api.get("/affiliate-partners/pie-chart", {
        params: { type, period },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching affiliate pie chart data:", error);
      throw error;
    }
  },

  // Get affiliate partners created by a specific staff member
  getAffiliatePartnersCreatedBy: async (staffId) => {
    try {
      console.log("Fetching affiliate partners created by staff ID:", staffId);
      const response = await api.get(`/affiliate-partners/staff/${staffId}`);
      console.log("API Response:", response.data);
      
      if (!response.data) {
        throw new Error("No data received from server");
      }
      
      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to fetch affiliate partners");
      }
      
      return response.data;
    } catch (error) {
      console.error("Error in getAffiliatePartnersCreatedBy:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  },
};
